import { useMemo } from "react";
import { useClinics } from "@/hooks/useClinics";
import {
  type MarketCountry,
  SUPPORTED_MARKETS,
  DEFAULT_MARKET,
  parseMarket,
} from "@/config/market";

// Markets that stay deep-linkable via ?country= but are hidden from the chooser
// UI. DE is reachable by URL param only; with just CH left the chooser collapses
// to one option and CountrySelector renders nothing (its `< 2` guard). Do NOT
// remove these from SUPPORTED_MARKETS — parseMarket needs them so ?country=de
// still resolves.
const CHOOSER_HIDDEN_MARKETS: readonly MarketCountry[] = ["de", "nl", "at"];

/**
 * The markets a user can actually pick in the chooser — the distinct `country`
 * values across active clinics, intersected with the markets the flow can
 * localise, minus any hidden (URL-param-only) markets.
 *
 * The default market is always included so the chooser never strands a user
 * with zero options while clinics are still loading.
 */
export const useAvailableMarkets = (): MarketCountry[] => {
  const { data: clinics } = useClinics();

  return useMemo(() => {
    const present = new Set<MarketCountry>([DEFAULT_MARKET]);
    for (const clinic of clinics ?? []) {
      const market = parseMarket(clinic.country);
      if (market) present.add(market);
    }
    return SUPPORTED_MARKETS.filter(
      (m) => present.has(m) && !CHOOSER_HIDDEN_MARKETS.includes(m),
    );
  }, [clinics]);
};
